import { Modal, notification } from "antd";
import { useFormik } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  DashboardConfigurationParameter,
  DashboardModes,
  useAddDashboardMutation,
} from "shared/api";
import { handleRequestError, noop } from "shared/helpers";
import { Colors } from "shared/themes";
import { FormInput } from "shared/ui/FormInput";
import { LayoutSelector, LayoutOption } from "shared/ui/LayoutSelector";

interface AddDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddDashboardModal: FC<AddDashboardModalProps> = ({ onClose, isOpen }) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const [addDashboard] = useAddDashboardMutation();
  const navigate = useNavigate();

  const addDashboardFormik = useFormik({
    initialValues: {
      name: null,
      configuration: [
        {
          configuration_parameter: DashboardConfigurationParameter.MODE,
          configuration_value: DashboardModes.ONE_COLUMN,
        },
      ],
    },
    enableReinitialize: true,
    onSubmit: noop,
  });

  const { values, handleBlur, handleChange, resetForm, setFieldValue } =
    addDashboardFormik;

  const handleSelectMode = (mode: DashboardModes) => {
    setFieldValue("configuration.0.configuration_value", mode);
  };

  const closeAndResetModal = () => {
    onClose();
    resetForm();
  };

  const layoutPreviewBox = (className: string) => (
    <div className={`w-full h-full rounded bg-blue-200 ${className}`} />
  );

  const layoutOptions: LayoutOption[] = [
    {
      mode: DashboardModes.ONE_COLUMN,
      label: t("dashboards.one-column"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          {layoutPreviewBox("flex-1")}
        </div>
      ),
    },
    {
      mode: DashboardModes.DOUBLE_COLUMNS,
      label: t("dashboards.two-columns"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          {layoutPreviewBox("flex-1")}
          {layoutPreviewBox("flex-1")}
        </div>
      ),
    },
    {
      mode: DashboardModes.THREE_COLUMNS,
      label: t("dashboards.three-columns"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          {layoutPreviewBox("flex-1")}
          {layoutPreviewBox("flex-1")}
          {layoutPreviewBox("flex-1")}
        </div>
      ),
    },
    {
      mode: DashboardModes.TWO_ONE,
      label: t("dashboards.two-one"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          <div className="flex-[2] h-full rounded bg-blue-200" />
          <div className="flex-1 h-full rounded bg-blue-200" />
        </div>
      ),
    },
    {
      mode: DashboardModes.ONE_TWO,
      label: t("dashboards.one-two"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          <div className="flex-1 h-full rounded bg-blue-200" />
          <div className="flex-[2] h-full rounded bg-blue-200" />
        </div>
      ),
    },
    {
      mode: DashboardModes.FOUR_COLUMNS,
      label: t("dashboards.four-columns"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          {layoutPreviewBox("flex-1")}
          {layoutPreviewBox("flex-1")}
          {layoutPreviewBox("flex-1")}
          {layoutPreviewBox("flex-1")}
        </div>
      ),
    },
    {
      mode: DashboardModes.THREE_ONE,
      label: t("dashboards.three-one"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          <div className="flex-[3] h-full rounded bg-blue-200" />
          <div className="flex-1 h-full rounded bg-blue-200" />
        </div>
      ),
    },
    {
      mode: DashboardModes.ONE_THREE,
      label: t("dashboards.one-three"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          <div className="flex-1 h-full rounded bg-blue-200" />
          <div className="flex-[3] h-full rounded bg-blue-200" />
        </div>
      ),
    },
    {
      mode: DashboardModes.ONE_ONE_TWO,
      label: t("dashboards.one-one-two"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          <div className="flex-1 h-full rounded bg-blue-200" />
          <div className="flex-1 h-full rounded bg-blue-200" />
          <div className="flex-[2] h-full rounded bg-blue-200" />
        </div>
      ),
    },
    {
      mode: DashboardModes.TWO_ONE_ONE,
      label: t("dashboards.two-one-one"),
      preview: (
        <div className="w-full h-full flex gap-1 px-2">
          <div className="flex-[2] h-full rounded bg-blue-200" />
          <div className="flex-1 h-full rounded bg-blue-200" />
          <div className="flex-1 h-full rounded bg-blue-200" />
        </div>
      ),
    },
    {
      mode: DashboardModes.FREE,
      label: t("dashboards.free-layout"),
      preview: (
        <div className="w-full h-full relative px-2">
          <div className="absolute top-0 left-2 w-5 h-5 rounded bg-blue-200" />
          <div className="absolute top-1 right-3 w-6 h-4 rounded bg-blue-300" />
          <div className="absolute bottom-0 left-5 w-7 h-5 rounded bg-blue-200" />
        </div>
      ),
    },
  ];

  const handleAddDashboard = async () => {
    try {
      const response = await addDashboard({
        name: values.name!,
        widgets: [],
        configuration: [
          {
            configuration_parameter: DashboardConfigurationParameter.MODE,
            configuration_value: values.configuration[0].configuration_value,
          },
        ],
      }).unwrap();

      if (response) navigate(`/dashboards/${response.id}`);

      api.success({
        message: t("dashboards.success-title"),
        description: t("dashboards.add-success-msg"),
      });
    } catch (error) {
      api.error({
        message: t("dashboards.error-title"),
        description: handleRequestError(error, t("dashboards.error-title")),
      });
    } finally {
      closeAndResetModal();
    }
  };

  const isDisabledForm =
    !values.name || !values.configuration[0].configuration_value;

  return (
    <>
      {contextHolder}
      <Modal
        open={isOpen}
        onCancel={closeAndResetModal}
        title={t("dashboards.add-dashboard")}
        okButtonProps={{
          disabled: isDisabledForm,
          style: {
            background: `${Colors.blue.primary}`,
          },
        }}
        onOk={handleAddDashboard}
      >
        <FormInput
          value={values.name}
          fieldName="name"
          placeholder={t("dashboards.add-dashboard-placeholder")}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />

        <LayoutSelector
          title={t("dashboards.select-mode")}
          selectedMode={
            values.configuration[0].configuration_value as DashboardModes
          }
          onSelectMode={handleSelectMode}
          options={layoutOptions}
        />
      </Modal>
    </>
  );
};

export { AddDashboardModal };
